import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const PromoCodePage = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [userCoins, setUserCoins] = useState(0);
  const [isUser, setIsUser] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  React.useEffect(() => {
    checkUser();
  }, []);

  React.useEffect(() => {
    if (isUser) {
      fetchUserCoins();
      checkEmailVerification();
    }
  }, [isUser]);

  const checkUser = () => {
    const token = localStorage.getItem('userToken');
    setIsUser(!!token);
  };

  const fetchUserCoins = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.get('/api/users/me/coins', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserCoins(response.data.coins || 0);
    } catch (error) {
      console.error('Error fetching user coins:', error);
    }
  };

  const checkEmailVerification = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.get('/api/users/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailVerified(response.data.emailVerified || response.data.email_verified || false);
    } catch (error) {
      console.error('Error checking email verification:', error);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendingEmail(true);
      const token = localStorage.getItem('userToken');
      const response = await axios.post('/api/auth/resend-verification', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(response.data.message || 'Doğrulama e-postası tekrar gönderildi');
      setMessageType('success');
    } catch (error) {
      setMessage(error.response?.data?.error || 'E-posta gönderilirken bir hata oluştu');
      setMessageType('error');
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isUser) {
      setMessage('Kod kullanmak için giriş yapmalısınız');
      setMessageType('error');
      return;
    }

    if (!emailVerified) {
      setMessage('Puan kodu kullanmak için e-posta adresinizi doğrulamanız gerekmektedir');
      setMessageType('error');
      return;
    }

    if (!code.trim()) {
      setMessage('Lütfen bir kod giriniz');
      setMessageType('error');
      return;
    }

    try {
      setLoading(true);
      setMessage('');
      const token = localStorage.getItem('userToken');
      
      const response = await axios.post(
        '/api/promo-codes/use',
        { code: code.trim() },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMessage(response.data.message || 'Kod başarıyla kullanıldı!');
      setMessageType('success');
      setCode('');
      setUserCoins(response.data.coins || userCoins);
      
      // Update userData
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      userData.coins = response.data.coins || userCoins;
      localStorage.setItem('userData', JSON.stringify(userData));
      window.dispatchEvent(new Event('userTokenChanged'));
    } catch (error) {
      console.error('Error using promo code:', error);
      setMessage(error.response?.data?.error || 'Kod kullanılırken bir hata oluştu');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full px-4 py-12">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gold mb-4 drop-shadow-[0_0_20px_rgba(212,175,55,0.8)]">
            Puan Kodu Kullan
          </h1>
          <p className="text-xl text-white/80 mb-6">
            Puan kodunuzu girin ve puanlarınızı alın!
          </p>
          
          {isUser ? (
            <div className="space-y-4">
              <div className="inline-flex items-center gap-3 bg-gold/20 px-6 py-3 rounded-xl border border-gold/30">
                <span className="text-gold font-bold text-2xl">
                  💰 {userCoins} Sagu Coin
                </span>
              </div>
              {!emailVerified && (
                <div className="bg-yellow-500/20 border-2 border-yellow-500/50 rounded-xl p-4">
                  <p className="text-yellow-400 font-semibold mb-2">⚠️ E-posta Doğrulama Gerekli</p>
                  <p className="text-white/80 text-sm mb-3">
                    Puan kodu kullanmak için e-posta adresinizi doğrulamanız gerekmektedir.
                  </p>
                  <button
                    onClick={handleResendVerification}
                    disabled={resendingEmail}
                    className="bg-yellow-500 hover:bg-yellow-600 text-navy-900 font-bold px-4 py-2 rounded-lg transition-all disabled:opacity-50"
                  >
                    {resendingEmail ? 'Gönderiliyor...' : 'Doğrulama E-postası Gönder'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-4 justify-center">
              <Link
                to="/login"
                className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-8 py-3 rounded-xl transition-all duration-300 shadow-gold"
              >
                Giriş Yap
              </Link>
              <Link
                to="/register"
                className="bg-transparent border-2 border-gold text-gold hover:bg-gold/10 font-bold px-8 py-3 rounded-xl transition-all duration-300"
              >
                Üye Ol
              </Link>
            </div>
          )}
        </div>

        {/* Promo Code Form */}
        <div className="bg-gradient-card rounded-xl p-8 border-2 border-gold/30 shadow-[0_0_30px_rgba(212,175,55,0.2)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-white font-semibold mb-3 text-lg">
                Puan Kodu
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="KODUNUZU GİRİN"
                className="w-full px-6 py-4 bg-navy-800 border-2 border-gold/30 rounded-xl text-white text-center text-xl font-bold tracking-wider placeholder-gray-500 focus:outline-none focus:border-gold transition-all duration-300"
                disabled={!isUser || loading}
                maxLength={50}
              />
            </div>

            {message && (
              <div className={`p-4 rounded-lg ${
                messageType === 'success' 
                  ? 'bg-green-500/20 border border-green-500/50 text-green-400' 
                  : 'bg-red-500/20 border border-red-500/50 text-red-400'
              }`}>
                <p className="font-semibold">{message}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={!isUser || loading || !code.trim()}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                isUser && !loading && code.trim()
                  ? 'bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 shadow-gold hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] transform hover:scale-105'
                  : 'bg-gray-600 text-gray-300 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-navy-900"></div>
                  İşleniyor...
                </span>
              ) : (
                'Kodu Kullan'
              )}
            </button>
          </form>

          {/* Info */}
          <div className="mt-8 p-4 bg-navy-800/50 rounded-lg border border-gold/20">
            <h3 className="text-gold font-semibold mb-2">ℹ️ Bilgilendirme</h3>
            <ul className="text-white/70 text-sm space-y-1">
              <li>• Her kod sadece bir kez kullanılabilir</li>
              <li>• Kodların süresi dolmuş olabilir</li>
              <li>• Kodların stok sınırı olabilir</li>
              <li>• Puanlar hesabınıza otomatik olarak eklenir</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoCodePage;
